// src/modules/callSync/pages/CallDevicesAdmin.jsx
// Which reps have a working phone — and, when they don't, exactly why.

import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Table, Tag, Typography, Space, Button, Input, message, Popconfirm, Tooltip,
} from 'antd';
import { MobileOutlined, ReloadOutlined, SearchOutlined, StopOutlined, CheckOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { fetchDevices, setDeviceStatus } from '../api';
import { usePermissions } from '../../../hooks/usePermissions';

const { Title, Text } = Typography;

const CONNECTION_COLORS = { ONLINE: 'green', STALE: 'orange', OFFLINE: 'red' };
const STATUS_COLORS = { ACTIVE: 'green', INACTIVE: 'default', REVOKED: 'red', REPLACED: 'purple' };

export default function CallDevicesAdmin() {
  const { can } = usePermissions();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchDevices(search ? { search } : {});
      setRows(res.data.data || []);
    } catch (err) {
      message.error(err?.response?.data?.message || 'Could not load devices');
    } finally { setLoading(false); }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (id, status) => {
    try {
      await setDeviceStatus(id, status);
      message.success(`Device ${status.toLowerCase()}`);
      load();
    } catch (err) {
      message.error(err?.response?.data?.message || 'Could not update the device');
    }
  };

  // The diagnostics a support person actually needs, rendered as chips.
  const capabilityTags = (c = {}) => {
    const items = [
      ['Phone', c.callPhoneGranted],
      ['Call log', c.readCallLogGranted],
      ['Notifications', c.postNotificationsGranted],
      ['SIM', c.simAvailable],
      ['Battery unrestricted', c.batteryOptimizationIgnored],
      // Without this the phone can still call, but the rep has to tap a
      // notification first — Android blocks background activity starts.
      ['Auto-dial', c.backgroundStartAllowed],
    ];
    return (
      <Space wrap size={2}>
        {items.map(([label, ok]) => (
          <Tooltip
            key={label}
            title={ok ? `${label}: granted` : `${label}: NOT granted — see the app's Settings screen on the device`}
          >
            <Tag color={ok ? 'green' : 'red'} style={{ marginInlineEnd: 2 }}>{label}</Tag>
          </Tooltip>
        ))}
      </Space>
    );
  };

  const columns = [
    {
      title: 'Sales Rep',
      dataIndex: ['user', 'name'],
      render: (v, r) => (
        <Space direction="vertical" size={0}>
          <Text strong>{v || '—'}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{r.user?.employeeCode || r.user?.email || ''}</Text>
        </Space>
      ),
    },
    {
      title: 'Device',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text>{r.deviceName || `${r.manufacturer || ''} ${r.model || ''}`.trim() || '—'}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Android {r.androidVersion || '?'} · app {r.appVersion || '?'}
          </Text>
        </Space>
      ),
    },
    { title: 'Branch', dataIndex: ['branch', 'name'], render: (v) => v || '—' },
    {
      title: 'Connection',
      dataIndex: 'connectionStatus',
      render: (v) => <Tag color={CONNECTION_COLORS[v] || 'default'}>{v}</Tag>,
      filters: Object.keys(CONNECTION_COLORS).map((k) => ({ text: k, value: k })),
      onFilter: (val, r) => r.connectionStatus === val,
    },
    {
      title: 'Registration',
      dataIndex: 'status',
      render: (v) => <Tag color={STATUS_COLORS[v] || 'default'}>{v}</Tag>,
    },
    {
      title: 'Last seen',
      dataIndex: 'lastHeartbeatAt',
      render: (v) => (v
        ? <Tooltip title={dayjs(v).format('DD MMM YYYY hh:mm:ss A')}>{dayjs(v).fromNow?.() || dayjs(v).format('DD MMM hh:mm A')}</Tooltip>
        : '—'),
    },
    { title: 'Permissions on device', render: (_, r) => capabilityTags(r.capabilities) },
    {
      title: '',
      render: (_, r) => (can('callDevices', 'edit') ? (
        r.status === 'ACTIVE' ? (
          <Popconfirm
            title="Revoke this device?"
            description="The phone will stop receiving call commands until it registers again."
            onConfirm={() => changeStatus(r._id, 'REVOKED')}
          >
            <Button size="small" danger icon={<StopOutlined />}>Revoke</Button>
          </Popconfirm>
        ) : (
          <Button size="small" icon={<CheckOutlined />} onClick={() => changeStatus(r._id, 'ACTIVE')}>
            Re-activate
          </Button>
        )
      ) : null),
    },
  ];

  return (
    <div style={{ padding: '8px 0' }}>
      <Space style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }}>
        <Title level={4} style={{ margin: 0 }}><MobileOutlined /> Call Sync Devices</Title>
        <Space>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Search device / model"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 220 }}
          />
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
        </Space>
      </Space>

      <Card size="small">
        <Table
          rowKey="_id"
          size="small"
          loading={loading}
          columns={columns}
          dataSource={rows}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 1200 }}
          locale={{ emptyText: 'No devices registered yet' }}
        />
      </Card>
    </div>
  );
}
